import { useEffect, useState } from "react";
import { KeySquare, Loader2, Copy, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { fmtDT, inputCls } from "../../lib/outreach";

export default function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/settings/api-keys", { name });
      setCreated(data.key);
      setName("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Anything using it will stop working.")) return;
    try {
      await api.delete(`/settings/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="api-keys-card" className="rounded-xl border border-slate-800 bg-[#131C31] p-6 mb-6">
      <h2 className="font-display font-bold text-lg mb-1 inline-flex items-center gap-2"><KeySquare className="w-5 h-5 text-amber-400" /> Outreach API Keys</h2>
      <p className="text-xs text-slate-400 mb-4">Your outreach assistant sends these in the <code className="text-amber-300">X-API-Key</code> header.</p>
      <form onSubmit={create} className="flex gap-2 mb-4">
        <input data-testid="api-key-name-input" required placeholder="Key name (e.g. Outreach assistant)" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        <button data-testid="api-key-create-button" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Generate
        </button>
      </form>
      {created && (
        <div data-testid="api-key-created" className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 mb-4">
          <p className="text-xs text-amber-200 mb-2">Copy this key now. It won't be shown again.</p>
          <div className="flex items-center gap-2">
            <code data-testid="api-key-value" className="text-xs text-white break-all flex-1">{created}</code>
            <button data-testid="api-key-copy-button" onClick={() => { navigator.clipboard.writeText(created); toast.success("Copied"); }} className="text-amber-400"><Copy className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      <ul className="divide-y divide-slate-800 text-sm">
        {keys.map((k) => (
          <li key={k.id} data-testid={`api-key-row-${k.id}`} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium text-white">{k.name}</span>
            <code className="text-xs text-slate-400">{k.prefix}…</code>
            <span className="text-xs text-slate-500">Last used: {fmtDT(k.last_used_at)}</span>
            {k.revoked_at ? <span className="text-xs text-red-300 ml-auto">Revoked</span> : (
              <button data-testid={`api-key-revoke-${k.id}`} onClick={() => revoke(k.id)} className="text-xs text-red-300 hover:text-red-200 ml-auto">Revoke</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
